// src/components/provider/WaitlistManager.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getProviderWaitingList } from '../../services/firestore';
import { 
  updateWaitlistEntryStatus, moveWaitlistEntry,
  getWaitlistByAgeCategory, getWaitlistStatistics
} from '../../services/enhancedFirestore';
import { useMessaging } from '../../contexts/MessagingContext';
import { useNavigate } from 'react-router-dom';
import { sendMessage } from '../../services/enhancedFirestore';

const WaitlistManager = () => {
  const { currentUser, userProfile } = useAuth();
  const { startConversation } = useMessaging();
  const navigate = useNavigate();
  
  // State
  const [waitlist, setWaitlist] = useState([]);
  const [statistics, setStatistics] = useState({
    total: 0, byStatus: {}, byAgeCategory: {}
  });
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('position');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filter, setFilter] = useState({ status: 'all', ageCategory: 'all' });
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusNotes, setStatusNotes] = useState('');
  
  // Config
  const statusClasses = {
    pending: 'status-pending',
    contacted: 'status-contacted',
    offered: 'status-offered',
    accepted: 'status-accepted',
    declined: 'status-declined',
    removed: 'status-removed'
  };
  
  const tabs = [
    { id: 'all', label: 'All' },
    { divider: true },
    { id: 'pending', label: 'Pending' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'offered', label: 'Offered' },
    { divider: true },
    { id: 'Infant/Toddler', label: 'Infant/Toddler' },
    { id: 'Preschool', label: 'Preschool' },
    { id: 'School-Age', label: 'School-Age' }
  ];
  
  const sortableColumns = [
    { id: 'position', label: '#' },
    { id: 'status', label: 'Status' },
    { id: 'childName', label: 'Child Name' },
    { id: 'age', label: 'Age' },
    { id: 'ageCategory', label: 'Age Category' },
    { id: 'date', label: 'Request Date' }
  ];
  
  const statusOptions = [
    { id: 'contacted', label: 'Mark as Contacted', className: 'status-contacted' },
    { id: 'offered', label: 'Mark as Offered', className: 'status-offered' },
    { id: 'accepted', label: 'Mark as Accepted', className: 'status-accepted' },
    { id: 'declined', label: 'Mark as Declined', className: 'status-declined' },
    { id: 'removed', label: 'Remove from Waitlist', className: 'status-removed' }
  ];
  
  // Helper to calculate age from dob (YYYY-MM-DD or Date)
  function calculateAge(dob) {
    if (!dob) return null;
    let birthDate;
    if (typeof dob === 'string') {
      birthDate = new Date(dob);
    } else if (dob instanceof Date) {
      birthDate = dob;
    } else {
      return null;
    }
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  // Helper to assign age category
  function getAgeCategory(age) {
    if (age === null || age === undefined || isNaN(age)) return 'N/A';
    if (age <= 2) return 'Infant/Toddler';
    if (age >= 3 && age <= 5) return 'Preschool';
    if (age >= 6) return 'School-Age';
    return 'N/A';
  }
  
  // Fetch waitlist entries on component mount
  useEffect(() => {
    const fetchWaitlist = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const waitlistEntries = await getProviderWaitingList(currentUser.uid);
        
        // Sort entries by position
        const sortedEntries = waitlistEntries.sort((a, b) => (a.position || 0) - (b.position || 0));
        setWaitlist(sortedEntries);
        
        // Get waitlist statistics
        const stats = await getWaitlistStatistics(currentUser.uid);
        setStatistics(stats);
      } catch (err) {
        console.error('Error fetching waitlist:', err);
        setError('Failed to load waitlist. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWaitlist();
  }, [currentUser]);
  
  // Handle tab change
  const handleTabChange = tab => {
    setActiveTab(tab);
    
    if (tab === 'all') {
      setFilter({ status: 'all', ageCategory: 'all' });
    } else if (['pending', 'contacted', 'offered', 'accepted', 'declined', 'removed'].includes(tab)) {
      setFilter(prev => ({ ...prev, status: tab }));
    } else if (['Infant/Toddler', 'Preschool', 'School-Age'].includes(tab)) {
      setFilter(prev => ({ ...prev, ageCategory: tab }));
    }
  };
  
  // Handle sort change
  const handleSortChange = field => {
    if (sortBy === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };
  
  // Handle status update
  const handleStatusUpdate = async status => {
    if (!selectedEntry) return;
    
    setStatusUpdateLoading(true);
    
    try {
      const updatedEntry = await updateWaitlistEntryStatus(selectedEntry.id, status, statusNotes);
      
      // Update waitlist
      setWaitlist(prev => prev.map(entry => entry.id === selectedEntry.id ? updatedEntry : entry));
      
      // Update statistics if status changed
      if (selectedEntry.status !== status) {
        setStatistics(prev => {
          const newStats = { ...prev };
          
          // Decrement old status count
          if (newStats.byStatus[selectedEntry.status]) {
            newStats.byStatus[selectedEntry.status]--;
          }
          
          // Increment new status count
          if (newStats.byStatus[status]) {
            newStats.byStatus[status]++;
          } else {
            newStats.byStatus[status] = 1;
          }
          
          return newStats;
        });
      }
      
      // Close details panel
      setSelectedEntry(null);
      setStatusNotes('');
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status. Please try again.');
    } finally {
      setStatusUpdateLoading(false);
    }
  };
  
  // Handle move up/down
  const handleMove = async direction => {
    if (!selectedEntry) return;
    
    try {
      await moveWaitlistEntry(currentUser.uid, selectedEntry.id, direction);
      
      // Refresh waitlist
      const waitlistEntries = await getProviderWaitingList(currentUser.uid);
      const sortedEntries = waitlistEntries.sort((a, b) => (a.position || 0) - (b.position || 0));
      setWaitlist(sortedEntries);
    } catch (err) {
      console.error(`Error moving entry ${direction}:`, err);
      setError(`Failed to move entry ${direction}. Please try again.`);
    }
  };
  
  // Handle message parent - FIXED VERSION
  // Update the handleMessageParent function in WaitlistManager.js

// Handle message parent - ENHANCED VERSION
const handleMessageParent = async () => {
  if (!selectedEntry || !selectedEntry.parentUid) return;
  
  try {
    setError(null);
    
    // Get parent and child names for the initial message
    const parentName = selectedEntry.requestData?.parentFirstName || 'parent';
    const childName = selectedEntry.childData?.name || 'your child';
    
    // Only pass the parent's UID to startConversation
    const conversation = await startConversation(selectedEntry.parentUid);
    
    if (conversation && conversation.id) {
      // Send an initial message to avoid the "Failed to load messages" error
      // This creates a message document in the messages collection
      const initialMessage = `Hello ${parentName}, I'm messaging you regarding ${childName}'s waitlist application. How can I help you?`;
      await sendMessage(conversation.id, currentUser.uid, initialMessage);
      
      // Navigate to the specific conversation
      navigate(`/messages/${conversation.id}`);
    } else {
      throw new Error('Failed to create conversation');
    }
  } catch (err) {
    console.error('Error starting conversation:', err);
    setError('Failed to start conversation. Please try again.');
  }
};
  
  // Format date for display
  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp instanceof Date 
      ? timestamp 
      : new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };
  
  // Filter and sort waitlist entries
  const filteredWaitlist = waitlist.filter(entry => {
    let calculatedAge = null;
    let entryAgeCategory = entry.childData?.ageCategory;
    if (entry.childData?.dob) {
      calculatedAge = calculateAge(entry.childData.dob);
      entryAgeCategory = getAgeCategory(calculatedAge);
    }
    if (filter.status !== 'all' && entry.status !== filter.status) return false;
    if (filter.ageCategory !== 'all' && entryAgeCategory !== filter.ageCategory) return false;
    return true;
  });
  
  const sortedWaitlist = [...filteredWaitlist].sort((a, b) => {
    const compareValues = {
      position: [(a.position || 0), (b.position || 0)],
      date: [
        a.createdAt ? new Date(a.createdAt.seconds * 1000) : new Date(0),
        b.createdAt ? new Date(b.createdAt.seconds * 1000) : new Date(0)
      ],
      childName: [a.childData?.name || '', b.childData?.name || ''],
      age: [parseInt(a.childData?.age || '0'), parseInt(b.childData?.age || '0')]
    };
    
    const [aVal, bVal] = compareValues[sortBy];
    
    if (sortBy === 'childName') {
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    } else {
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
  });
  
  // Reusable components
  const StatisticCard = ({ title, children }) => (
    <div className="statistics-card">
      <h3>{title}</h3>
      {children}
    </div>
  );
  
  const StatItem = ({ label, count, className }) => (
    <div className="stat-item">
      <span className={`${className || ''}`}>{label}</span>
      <span className="stat-count">{count || 0}</span>
    </div>
  );
  
  const WaitlistRow = ({ entry, displayPosition }) => {
    // Calculate age and category from dob if available
    const dob = entry.childData?.dob;
    const calculatedAge = dob ? calculateAge(dob) : null;
    const displayAge = calculatedAge !== null ? calculatedAge : (entry.childData?.age || 'N/A');
    const calculatedCategory = calculatedAge !== null ? getAgeCategory(calculatedAge) : (entry.childData?.ageCategory || 'N/A');
    return (
      <tr 
        className={`waitlist-row ${entry.id === selectedEntry?.id ? 'selected' : ''}`}
        onClick={() => {
          if (entry.id !== selectedEntry?.id) setSelectedEntry(entry);
        }}
      >
        <td className="position-column">{displayPosition}</td>
        <td>
          <span className={`status-badge ${statusClasses[entry.status] || ''}`}>{entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}</span>
        </td>
        <td>{entry.childData?.name || 'N/A'}</td>
        <td>{displayAge}</td>
        <td>{calculatedCategory}</td>
        <td>{formatDate(entry.createdAt)}</td>
        <td className="action-buttons">
          <button 
            className="view-details-button"
            onClick={e => {
              e.stopPropagation();
              if (entry.id !== selectedEntry?.id) {
                setSelectedEntry(entry);
              }
            }}
          >
            View Details
          </button>
        </td>
      </tr>
    );
  };
  
  const DetailSection = ({ title, children }) => (
    <div className="detail-section">
      <h4>{title}</h4>
      {children}
    </div>
  );
  
  return (
    <div className="waitlist-manager">
      <h1>Waitlist Management</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading waitlist...</p>
        </div>
      ) : (
        <>
          {/* Waitlist Statistics */}
          <div className="waitlist-statistics">
            <StatisticCard title="Total Entries">
              <div className="stat-value">{statistics.total}</div>
            </StatisticCard>
            
            <StatisticCard title="By Status">
              <div className="stat-breakdown">
                <StatItem label="Pending" count={statistics.byStatus.pending} className="status-label status-pending" />
                <StatItem label="Contacted" count={statistics.byStatus.contacted} className="status-label status-contacted" />
                <StatItem label="Offered" count={statistics.byStatus.offered} className="status-label status-offered" />
                <StatItem label="Accepted" count={statistics.byStatus.accepted} className="status-label status-accepted" />
              </div>
            </StatisticCard>
            
            <StatisticCard title="By Age Category">
              <div className="stat-breakdown">
                <StatItem label="Infant/Toddler" count={statistics.byAgeCategory['Infant/Toddler']} className="age-label" />
                <StatItem label="Preschool" count={statistics.byAgeCategory['Preschool']} className="age-label" />
                <StatItem label="School-Age" count={statistics.byAgeCategory['School-Age']} className="age-label" />
              </div>
            </StatisticCard>
          </div>
          
          {/* Tabs */}
          <div className="waitlist-tabs">
            {tabs.map(tab => 
              tab.divider ? (
                <div key={`divider-${tab.id || Math.random()}`} className="tab-divider"></div>
              ) : (
                <button 
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              )
            )}
          </div>
          
          {/* Waitlist Table */}
          <div className="waitlist-table-container">
            <table className="waitlist-table">
              <thead>
                <tr>
                  {sortableColumns.map(column => (
                    <th key={column.id} className={column.id === 'position' ? 'position-column' : ''}>
                      <button 
                        className={`sort-button ${sortBy === column.id ? 'active' : ''}`}
                        onClick={() => handleSortChange(column.id)}
                      >
                        {column.label}
                        {sortBy === column.id && (
                          <span className={`sort-indicator ${sortOrder}`}>
                            {sortOrder === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </button>
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedWaitlist.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-table">
                      No waitlist entries found
                    </td>
                  </tr>
                ) : (
                  sortedWaitlist.map((entry, idx) => <WaitlistRow key={entry.id} entry={entry} displayPosition={idx+1} />)
                )}
              </tbody>
            </table>
          </div>
          
          {/* Entry Details Panel */}
          {selectedEntry && (
            <div className="entry-details-panel">
              <div className="panel-header">
                <h3>Waitlist Entry Details</h3>
                <button className="close-panel-button" onClick={() => setSelectedEntry(null)}>✕</button>
              </div>
              
              <div className="panel-content">
                <div className="entry-details">
                  <DetailSection title="Child Information">
                    <p><strong>First Name:</strong> {selectedEntry.childData?.firstName || 'N/A'}</p>
                    <p><strong>Last Name:</strong> {selectedEntry.childData?.lastName || 'N/A'}</p>
                    <p><strong>Full Name:</strong> {selectedEntry.childData?.name || 'N/A'}</p>
                    <p><strong>Date of Birth:</strong> {selectedEntry.childData?.dob || 'N/A'}</p>
                    <p><strong>Gender:</strong> {selectedEntry.childData?.gender || 'N/A'}</p>
                    <p><strong>Requires Extra Support:</strong> {selectedEntry.childData?.extraSupport || 'N/A'}</p>
                    {(selectedEntry.childData?.extraSupport === 'Yes' || selectedEntry.childData?.extraSupport === 'Unsure') && (
                      <p><strong>Extra Support Explanation:</strong> {selectedEntry.childData?.extraSupportExplain || 'N/A'}</p>
                    )}
                    <p><strong>Age:</strong> {selectedEntry.childData?.dob ? calculateAge(selectedEntry.childData.dob) : (selectedEntry.childData?.age || 'N/A')}</p>
                    <p><strong>Age Category:</strong> {selectedEntry.childData?.dob ? getAgeCategory(calculateAge(selectedEntry.childData.dob)) : (selectedEntry.childData?.ageCategory || 'N/A')}</p>
                  </DetailSection>
                  <DetailSection title="Parent/Guardian Information">
                    <p><strong>First Name:</strong> {selectedEntry.requestData?.parentFirstName || 'N/A'}</p>
                    <p><strong>Last Name:</strong> {selectedEntry.requestData?.parentLastName || 'N/A'}</p>
                    <p><strong>Relationship to Child:</strong> {selectedEntry.requestData?.relationship || 'N/A'}</p>
                    <p><strong>Email:</strong> {selectedEntry.requestData?.email || 'N/A'}</p>
                    <p><strong>Phone:</strong> {selectedEntry.requestData?.phone || 'N/A'}</p>
                    <p><strong>Address:</strong> {selectedEntry.requestData?.address || 'N/A'}</p>
                  </DetailSection>
                  <DetailSection title="Enrollment Information">
                    <p><strong>Preferred Start Date:</strong> {selectedEntry.requestData?.enrollmentStartDate || selectedEntry.requestData?.startDate || 'N/A'}</p>
                    <p><strong>Additional Notes:</strong> {selectedEntry.requestData?.enrollmentNotes || selectedEntry.requestData?.notes || 'N/A'}</p>
                  </DetailSection>
                  <DetailSection title="Request Information">
                    <p>
                      <strong>Status:</strong> 
                      <span className={`status-badge ${statusClasses[selectedEntry.status] || ''}`}>
                        {selectedEntry.status.charAt(0).toUpperCase() + selectedEntry.status.slice(1)}
                      </span>
                    </p>
                    <p><strong>Position:</strong> {selectedEntry.position || 'N/A'}</p>
                    <p><strong>Date Requested:</strong> {formatDate(selectedEntry.createdAt)}</p>
                  </DetailSection>
                  {selectedEntry.notes && (
                    <DetailSection title="Provider Notes">
                      <p>{selectedEntry.notes}</p>
                    </DetailSection>
                  )}
                </div>
                
                <div className="entry-actions">
                  <div className="position-actions">
                    <h4>Waitlist Position</h4>
                    <div className="button-group">
                      <button 
                        className="move-up-button"
                        onClick={() => handleMove('up')}
                        disabled={selectedEntry.position <= 1}
                      >
                        Move Up
                      </button>
                      <button 
                        className="move-down-button"
                        onClick={() => handleMove('down')}
                      >
                        Move Down
                      </button>
                    </div>
                  </div>
                  
                  <div className="status-actions">
                    <h4>Update Status</h4>
                    <div className="status-notes-input">
                      <label htmlFor="statusNotes">Add Notes:</label>
                      <textarea
                        id="statusNotes" value={statusNotes} rows={3}
                        onChange={e => setStatusNotes(e.target.value)}
                        placeholder="Add notes about this status update..."
                      />
                    </div>
                    <div className="status-buttons">
                      {statusOptions.map(option => (
                        <button 
                          key={option.id}
                          className={`status-button ${option.className}`}
                          onClick={() => handleStatusUpdate(option.id)}
                          disabled={statusUpdateLoading || selectedEntry.status === option.id}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="communication-actions">
                    <h4>Communication</h4>
                    <button className="message-button" onClick={handleMessageParent}>Message Parent</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WaitlistManager;